import React, {useState, Fragment, useContext, useEffect} from 'react';
import {Table, Tag, Typography, Popconfirm, Button, Input, Select, Checkbox, Row, Col, notification, Space, Modal, Form} from 'antd';
import {SchemaPropType} from '../schema-prop-type';
import {remove, isUndefined, identity} from 'lodash';
import {EditableCell} from '../editable-cell';
import {DeleteOutlined, CaretUpOutlined} from '@ant-design/icons';
import {OpenAPIRef} from '../openapi-ref';
import {AddSchemaRefFieldDialog} from '../add-schema-ref-field-dialog';
import {AddSchemaFieldBulkDialog} from '../add-schema-field-bulk-dialog';
import {MarkdownEditor} from '../markdown-editor';
import {MarkdownView} from '../markdown-block';
import {OpenApiContext} from '../../openapi-context';
import {useRouteMatch} from 'react-router-dom';

export const FLAG_COLORS = {
    required: 'volcano',
    deprecated: 'red',
    allowEmptyValue: 'green',
};

function PromoteParameterDialog({parameter}) {
    const {dispatch} = useContext(OpenApiContext);
    const [visible, setVisible] = useState();
    const [form] = Form.useForm();

    function handleCancel() {
        form.resetFields();
        setVisible(false);
    }

    async function promoteParam() {
        try {
            const values = await form.validateFields();
            const definition = {
                name: values.name,
                in: parameter.in,
                type: parameter.type,
                description: values.description,
                required: !!parameter.required,
                deprecated: !!parameter.deprecated,
                allowEmptyValue: !!parameter.allowEmptyValue,
            };

            dispatch({type: 'add-parameter-definition', definition});
            setVisible(false);
            notification.success({title: `Parameter ${definition.name} was successfully promoted`, placement: 'bottomRight'});
        } catch (err) {
            console.error(err);
        }
    }

    return (
        <Fragment>
            <Button className="promoteButton" size="small" type="primary" icon={<CaretUpOutlined />} onClick={() => setVisible(true)}>
                Promote
            </Button>
            {visible && (
                <Modal
                    open={visible}
                    title={
                        <Typography.Title level={4}>
                            <CaretUpOutlined size="large" /> Promote Parameter
                        </Typography.Title>
                    }
                    footer={[
                        <Button className="addRef" key="back" onClick={handleCancel}>
                            Cancel
                        </Button>,
                        <Button className="addField" key="submit" type="primary" onClick={promoteParam}>
                            Promote
                        </Button>,
                    ]}
                    onCancel={handleCancel}
                    onOk={promoteParam}>
                    <Typography.Paragraph>Promoting a parameter will add it to your definitions so that you can reference it everywhere in your spec.</Typography.Paragraph>
                    <Form initialValues={parameter} layout="vertical" form={form}>
                        <Form.Item
                            label="Name"
                            name="name"
                            rules={[
                                {
                                    required: true,
                                    message: 'Name is required',
                                },
                            ]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="Description" name="description">
                            <MarkdownEditor height={150} />
                        </Form.Item>
                    </Form>
                </Modal>
            )}
        </Fragment>
    );
}

export function NameEditor({record, onSave}) {
    console.log('editing parameter name', record);

    return (
        <Fragment>
            <Row>
                <Col xs={18}>
                    <Input defaultValue={record.name} placeholder="parameter name" onBlur={(e) => onSave({type: 'param-rename', from: record.name, to: e.target.value})} />
                </Col>
                <Col xs={5} offset={1}>
                    <Select
                        style={{width: '100%'}}
                        defaultValue={record.type || record.schema.type}
                        onChange={(val) => onSave({type: 'update-param', name: record.name, fields: {schema: {type: val}}})}>
                        <Select.Option value="string">string</Select.Option>
                        <Select.Option value="number">number</Select.Option>
                        <Select.Option value="boolean">boolean</Select.Option>
                        <Select.Option value="object">object</Select.Option>
                        <Select.Option value="array">array</Select.Option>
                    </Select>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <MarkdownEditor value={record.description} height={150} onChange={(val) => onSave({type: 'update-param', name: record.name, fields: {description: val}})} />
                    <Input defaultValue={record.example} placeholder="Example value" onChange={(e) => onSave({type: 'update-param', name: record.name, fields: {example: e.target.value}})} />
                </Col>
            </Row>
        </Fragment>
    );
}
export function InEditor({record, onSave}) {
    return (
        <Select style={{width: '100%'}} defaultValue={record.in} onChange={(val) => onSave({type: 'update-param', name: record.name, fields: {in: val}})}>
            <Select.Option value="path">path</Select.Option>
            <Select.Option value="query">query</Select.Option>
            <Select.Option value="header">header</Select.Option>
            <Select.Option value="cookie">cookie</Select.Option>
        </Select>
    );
}

export function FlagsEditor({record, onSave}) {
    const stateChecked = (key) => (record.tags ? !!record.tags.find((s) => s.text === key) : record[key]);

    function handleChecked(flag, field) {
        return function (e) {
            if (record.tags) {
                if (!e.target.checked) {
                    remove(record.tags, (s) => s.text === flag);
                } else {
                    record.tags.push({text: flag, color: FLAG_COLORS[flag]});
                }
            }

            const changeSet = {
                type: 'update-param',
                name: record.name,
                fields: {},
                tags: record.tags,
            };
            changeSet.fields[field || flag] = e.target.checked;
            onSave(changeSet);
        };
    }

    return (
        <div>
            <Checkbox defaultChecked={stateChecked('deprecated')} onChange={handleChecked('deprecated')}>
                Deprecated
            </Checkbox>
            <br />
            <Checkbox defaultChecked={stateChecked('required')} onChange={handleChecked('required')}>
                Required
            </Checkbox>
            <br />
            <Checkbox defaultChecked={stateChecked('allow-empty') || stateChecked('allowEmptyValue')} onChange={handleChecked('allow-empty', 'allowEmptyValue')}>
                Allow Empty Value
            </Checkbox>
            <br />
        </div>
    );
}

export function ParameterTable({parameters, editMode, triggerEdit, onChange}) {
    const [editing, setEditing] = useState();
    const [transformedParameters, setTransformedParameters] = useState([]);
    const openapiContext = useContext(OpenApiContext);
    const {params} = useRouteMatch('/:env/standards/:category/:key/:version/:view/:element');

    const rowSelectionConfig = () => {
        return editMode || (!editMode && triggerEdit)
            ? {
                  type: 'radio',
                  selectedRowKeys: editing ? [editing] : [],
                  onSelect(row) {
                      if (!editMode && triggerEdit) {
                          triggerEdit();
                      }
                      setEditing(row.key || row.name);
                  },
              }
            : null;
    };

    // Always route through EditableCell so onCell props left over from
    // the previous edit-mode render do not leak to <td> via antd's
    // default Cell2 on the next render (React warns on unknown DOM
    // attrs like `editor` and `editMode`).
    const components = () => ({body: {cell: EditableCell}});
    const removeParameter = (param) => () => {
        onChange({type: 'rm-op-parameter', name: param.name, $ref: param.$ref});
    };

    const addParameter = (spec) => {
        if (!Array.isArray(spec)) {
            spec = [spec];
        }
        spec = spec
            .map((newParam) => {
                newParam.key = newParam.key || newParam.name;
                newParam.in = newParam.in || 'path';
                const exists = !!parameters.find((p) => p.name === newParam.name);
                if (exists) {
                    notification.error({title: `Parameter ${newParam.name} already exists`, placement: 'bottomRight'});
                }
                return exists ? null : newParam;
            })
            .filter(identity);

        if (spec.length > 0) {
            onChange({type: 'add-op-parameter', param: spec});
        }
    };

    let columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            editor: (props) => <NameEditor {...props} />,
            render: (name, record) => {
                return (
                    <div>
                        <Typography.Paragraph className="propertyName">
                            {name}
                            <span className="propertyType">
                                : <SchemaPropType record={record.schema} />
                            </span>
                        </Typography.Paragraph>
                        {record.summary && (
                            <Typography.Paragraph className="propertySummary">
                                <Typography.Text strong>{record.summary}</Typography.Text>
                            </Typography.Paragraph>
                        )}
                        {record.description && <MarkdownView source={record.description} />}
                        {record.$ref && (
                            <pre>
                                Reference to <OpenAPIRef link={record.$ref} componentType="parameters" />
                            </pre>
                        )}
                        {record.example !== undefined && record.example !== null && (
                            <Typography.Text code ellipsis={true}>
                                ex: {typeof record.example === 'object' ? JSON.stringify(record.example) : String(record.example)}
                            </Typography.Text>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'In',
            editor: (props) => <InEditor {...props} />,
            dataIndex: 'in',
        },
        {
            title: 'Flags',
            editor: (props) => <FlagsEditor {...props} />,
            render: (param) =>
                !isUndefined(param.tags) ? (
                    param.tags.map((t) => (
                        <Tag key={t.text} color={t.color}>
                            {t.text}
                        </Tag>
                    ))
                ) : (
                    <Fragment>
                        {param.required && (
                            <Tag key="required" color={FLAG_COLORS.required}>
                                Required
                            </Tag>
                        )}
                        {param.allowEmptyValue && (
                            <Tag key="allowEmptyValue" color={FLAG_COLORS.allowEmptyValue}>
                                Allow Empty Value
                            </Tag>
                        )}
                        {param.deprecated && (
                            <Tag key="deprecated" color={FLAG_COLORS.deprecated}>
                                Deprecated
                            </Tag>
                        )}
                    </Fragment>
                ),
        },
    ];

    if (editMode) {
        columns.push({
            width: '10%',
            render: (_, record) => (
                <Space>
                    {!record.$ref && <PromoteParameterDialog parameter={record} />}
                    <Popconfirm okType="default" title={`Remove field ${record.name} from schema?`} onConfirm={removeParameter(record)}>
                        <Button className="deleteIcon" size="small" type="danger" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        });

        columns = columns.map((col) =>
            Object.assign(col, {
                onCell: (record) => ({
                    record,
                    editMode,
                    editing,
                    editor: col.editor,
                    dataIndex: col.dataIndex,
                    title: col.title,
                    handleSave: (action) => {
                        if (action.type === 'param-rename') {
                            setEditing(action.to);
                        }
                        return onChange(action);
                    },
                }),
            })
        );
    }

    useEffect(() => {
        async function transformParameters() {
            setTransformedParameters(
                await Promise.all(
                    parameters.map(async (param) => {
                        if (param.$ref) {
                            // 3.1 Reference Object permits description/summary
                            // siblings next to $ref that override the target.
                            // Preserve them before the lookup result clobbers
                            // the record.
                            const siblings = {
                                $ref: param.$ref,
                                description: param.description,
                                summary: param.summary,
                            };
                            const resolved = await openapiContext.state.lookup(param.$ref, params);
                            param = {...(resolved || {})};
                            param.$ref = siblings.$ref;
                            if (siblings.description) param.description = siblings.description;
                            if (siblings.summary) param.summary = siblings.summary;
                        }
                        param.key = param.name;
                        return param;
                    })
                )
            );
        }
        if (parameters) {
            transformParameters(parameters);
        }
    }, [parameters]);

    return (
        <Fragment>
            <Table
                id="paramaterTable"
                size="small"
                components={components()}
                dataSource={transformedParameters}
                rowSelection={rowSelectionConfig()}
                columns={columns}
                pagination={false}
                className="columnStyle"
            />
            {editMode && <AddSchemaFieldBulkDialog onAddField={addParameter} title="Add Parameter" />}
            {editMode && <AddSchemaRefFieldDialog onAddField={addParameter} nameLabel="Param Name" forceFieldName={true} types={['parameters']} />}
        </Fragment>
    );
}
